import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { twkAnimations, gsapEffects } from './scrollAnimations-v2--effects';

gsap.registerPlugin(ScrollTrigger);

ScrollTrigger.config({
	ignoreMobileResize: true // Prevént refreshes on small height changes (like address bar hide/show)
});


export default function() {
	// The inline <head> failsafe already revealed everything (JS was too slow): don't hide it again.
	if ( document.documentElement.classList.contains('twk-failsafe') ) {
		document.querySelectorAll('[twk-aos]').forEach( el => el.setAttribute('twk-aos-status', 'animated') );
		return;
	}

	twkAnimations() // Gsap custom effects to add to timelines.

	// Immediately take control of opacity to prevent CSS race condition
	// This runs synchronously before matchMedia async setup
	gsap.set('[twk-aos]', { autoAlpha: 0 });

	let mm = gsap.matchMedia()

	// Breakpoints
	const screenXS  = 340;
	const screenSM  = 576;
	const screenMD  = 768;
	const screenLG  = 992;
	const screenXL  = 1280;
	const screen2XL = 1536;
	const screen3XL = 1920;

	mm.add(
		{
			// set up any number of arbitrarily-named conditions. The function below will be called when ANY of them match.
			is_: `(max-width: ${screenXS - 1}px)`,
			isXS: `(min-width: ${screenXS}px)`,
			isSM: `(min-width: ${screenSM}px)`,
			isMD: `(min-width: ${screenMD}px)`,
			isLG: `(min-width: ${screenLG}px)`,
			isXL: `(min-width: ${screenXL}px)`,
			is2XL: `(min-width: ${screen2XL}px)`,
			is3XL: `(min-width: ${screen3XL}px)`,
			reduceMotion: "(prefers-reduced-motion: reduce)",
		},
		( context ) => {
			// context.conditions has a boolean property for each condition defined above indicating if it's matched or not.
			let { is_, isXS, isSM, isMD, isLG, isXL, is2XL, is3XL, reduceMotion  } = context.conditions;

			if ( reduceMotion ) {
				console.info('Reduced motion is ON');
				// The initial autoAlpha:0 also set visibility:hidden, which the reduced-motion CSS does not override.
				gsap.utils.toArray('[twk-aos]').forEach( el => {
					gsap.set(el, { autoAlpha: 1 });
					el.setAttribute('twk-aos-status', 'animated');
				});
			} else {
				let sections = gsap.utils.toArray('[twk-aos]');

				sections.forEach(section => {
					if ( is3XL && section.getAttribute('twk-aos-3xl') ) {
						getSectionAnimation( section, '3xl' );
					} else if ( is2XL && section.getAttribute('twk-aos-2xl') ) {
						getSectionAnimation( section, '2xl' );
					} else if ( isXL && section.getAttribute('twk-aos-xl') ) {
						getSectionAnimation( section, 'xl' );
					} else if ( isLG && section.getAttribute('twk-aos-lg') ) {
						getSectionAnimation( section, 'lg' );
					} else if ( isMD && section.getAttribute('twk-aos-md') ) {
						getSectionAnimation( section,'md' );
					} else if ( isSM && section.getAttribute('twk-aos-sm') ) {
						getSectionAnimation( section,'sm' );
					} else if ( isXS && section.getAttribute('twk-aos-xs') ) {
						getSectionAnimation( section, 'xs' );
					} else if ( is_ && section.getAttribute('twk-aos-_') ) {
						getSectionAnimation( section, '_' );
					} else {
						getSectionAnimation( section );
					}
				});
			}

			// After setting up all animations, refresh once
			ScrollTrigger.sort();
			ScrollTrigger.refresh();

			return () => {
				// Cleanup is handled by matchMedia (context.revert())
			};
		}
	);

	// Setup succeeded: the failsafe is no longer needed.
	clearTimeout(window.__twkFailsafe);

	// Create an Observer instance to check if the page changes in height
	// and refresh ScrollTrigger so the markers are in the correct position.
	let refreshTimeout;
	const resizeObserver = new ResizeObserver( () => {
			clearTimeout(refreshTimeout);
			refreshTimeout = setTimeout(() => {
				ScrollTrigger.refresh();
			}, 200);
		}
	)
	resizeObserver.observe( document.body )

	// Final refresh on window load to ensure everything is in place
	window.addEventListener('load', () => {
		ScrollTrigger.refresh();
	});


	function getSectionAnimation( section, screenSize = '' ) {
		let onFirstBlock = false;
		
		if ( section.closest('section') !== null ){
			const parent         = document.querySelector('main.main');
			const firstSectionId = parent?.firstElementChild?.id

			if ( section.closest('section').id === firstSectionId ) {
				onFirstBlock = true;
			}
		}

		section.setAttribute('twk-aos-status', 'animate');  

		// Use percentage-based triggers for stability across different screen tallness
		let twkAosTrigger   = ( onFirstBlock ? 'top 98%' : 'top 88%' );
		let twkAosDuration  = 0.8;
		let twkAosDelay     = '';
		let twkAosMarker    = false;
		let twkAosMarkerId  = 'section';
		let twkAosIsStagger = false;
		let twkAosStagger   = 0.2;
		let twkAosEase      = 'power1.out';

		if (
			section.getAttribute('twk-aos') === 'slide-and-mask-left' ||
			section.getAttribute('twk-aos') === 'slide-and-mask-left--banner' ||
			section.getAttribute('twk-aos') === 'slide-and-mask-right' ||
			section.getAttribute('twk-aos') === 'slide-and-mask-right--banner'
		){
			twkAosEase = 'power3.inOut';
			twkAosDuration  = 1.7;
		}

		// Check for custom triggers from attributes
		if ( section.getAttribute('twk-aos-' + screenSize + '-trigger') !== null ){
			twkAosTrigger = section.getAttribute('twk-aos-' + screenSize + '-trigger');
		} else if( section.getAttribute('twk-aos-trigger') !== null ) {
			twkAosTrigger = section.getAttribute('twk-aos-trigger');
		}

		if ( section.getAttribute('twk-aos-' + screenSize + '-duration') !== null ){
			twkAosDuration = section.getAttribute('twk-aos-' + screenSize + '-duration') / 1000; 
		} else if( section.getAttribute('twk-aos-duration') !== null ) {
			twkAosDuration = section.getAttribute('twk-aos-duration') / 1000;                    
		}
		if ( section.getAttribute('twk-aos-' + screenSize + '-delay') !== null ){
			twkAosDelay = section.getAttribute('twk-aos-' + screenSize + '-delay') / 1000;       
		} else if( section.getAttribute('twk-aos-delay') !== null ) {
			twkAosDelay = section.getAttribute('twk-aos-delay') / 1000;                          
		}
		if ( section.getAttribute('twk-aos-marker') !== null ){
			twkAosMarker  = true;
			twkAosMarkerId = section.getAttribute('twk-aos-marker');
		}
		if ( section.getAttribute('twk-aos-stagger') !== null ){
			twkAosIsStagger = true;
			twkAosStagger = section.getAttribute('twk-aos-stagger') / 1000;                      
		}
		if (section.getAttribute('twk-aos-ease') !== null) {
			twkAosEase = section.getAttribute('twk-aos-ease')
		}

		let twkAosAnchor = section;
		if ( section.getAttribute('twk-aos-anchor') !== null ) {
			twkAosAnchor = section.getAttribute('twk-aos-anchor');
		}

		if ( section ) {
			let tl = gsap.timeline({
				autoRemoveChildren: true,
				defaults: {
					duration: twkAosDuration,
					ease: twkAosEase,
					overwrite: "auto",
				},
				scrollTrigger: {
					trigger: twkAosAnchor,
					fastScrollEnd: true,
					start: twkAosTrigger,
					end: "bottom 80%",
					id: twkAosMarkerId,
					markers: twkAosMarker,
					invalidateOnRefresh: true,
					once: true,
					immediateRender: true,
					onLeave: () => {
						section.setAttribute('twk-aos-status', 'animated');
					},
					onEnter: () => {
						section.setAttribute('twk-aos-status', 'animating');
					},
				},
				onComplete: () => {
					section.setAttribute('twk-aos-status', 'animated');
				}
			});
			
			const animationName =  ( screenSize != '' ? section.getAttribute('twk-aos-' + screenSize) : section.getAttribute('twk-aos') ) 
		
			// Effects mapping
			const animationNames = [];
			gsapEffects?.forEach(effect => {
				animationNames.push(effect.id);
			});

			const animationFunctions = {};
			animationNames.forEach(name => {
				animationFunctions[name] = (target, config) => tl[name](target, config);
			});
			
			if ( twkAosIsStagger ) {
				let sectionConfig = {
					duration: twkAosDuration,
					delay: twkAosDelay,
					ease: twkAosEase,
					stagger: twkAosStagger,
				}

				if ( animationName in animationFunctions ) {
					const allChildren = section.children;
					const allChildrenArray = Array.from(allChildren);
					const filteredChildren = allChildrenArray.filter(child => !child.hasAttribute('twk-aos-ignore'));

					const effect = gsapEffects.find(e => e.id === animationName);
					if ( effect && effect.props1 ) {
						gsap.set(filteredChildren, effect.props1);
					}

					animationFunctions[animationName](section, { duration: 0 });  
					animationFunctions[animationName](filteredChildren, sectionConfig);
				} else {
					console.warn("Invalid animationName: " + animationName);
				}
			} else {
				let sectionConfig = {
					duration: twkAosDuration,
					delay: twkAosDelay,
					ease: twkAosEase,
				}

				if ( animationName in animationFunctions ) {
					const effect = gsapEffects.find(e => e.id === animationName);
					if ( effect && effect.props1 ) {
						gsap.set(section, effect.props1);
					}
					animationFunctions[animationName](section, sectionConfig);
				} else {
					console.warn("Invalid animationName: " + animationName);
				}
			}
			
		} else {
			console.error('Section element not found.');
		}
	}
}
